import React, { Fragment, useReducer, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Select from "react-select";

import ai from "../../helpers/axios";
import Loader from "../loader";
import ProspectHeader from "./single-prospect-header";
import TextInput from "../text-input";
import currencyFormatter from "../../helpers/currencyFormatter";
import moment from "moment-timezone";
import CreatableSelect from "react-select/creatable";
import SingleProspectAddressesTable from "./single-prospect-addresses-table";
import SingleProspectEmailsTable from "./single-prospect-emails-table";
import SingleProspectPhonesTable from "./single-prospect-phones-table";
import SingleProspectPassengersTable from "./single-prospect-passengers-table";
import SingleProspectPetsTable from "./single-prospect-pets-table";
import SingleProspectNotesTable from "./single-prospect-notes-table";
import SingleProspectAuthorizedSignersTable from "./single-prospect-authorized-signers-table";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";
import SingleProspectTripHistory from "./single-prospect-trip-history";
import DatePicker from "react-date-picker";
import genders from "../../helpers/genders";
import {
  MEMBERSHIP_TIER_DEFAULTS,
  DEPOSIT_TIER_OPTIONS,
  BENEFITS_TIER_OPTIONS,
  MEMBERSHIP_PERCENT_OPTIONS,
  formatPercent,
  parsePercentInput,
  isValidMembershipPercent,
  emptyMembership,
} from "../../helpers/membership";
import MembershipActivationModal from "./membership-activation-modal";
import getApiErrorMessage from "../../helpers/get-api-error-message";

let toastOptions = {
  position: "top-right",
  autoClose: 6000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const parseStage = (stage, stageOptions) => {
  const option = stageOptions.find((option) => option.value === stage);

  return option || null;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "toggleEditInModal": {
      return {
        ...state,
        editableField:
          !action.modal || state.editableField === action.modal
            ? null
            : action.modal,
      };
    }
    case "dataDownloaded": {
      let data = action.value;

      const stage = parseStage(data.stage, state.stageOptions);

      if (data?.additionalUsers?.length) {
        data.additionalUsers = data.additionalUsers.map((i) => {
          if (i?.relationship) {
            i.relationship = {
              label: i.relationship,
              value: i.relationship,
            };
          }

          return {
            ...i,
          };
        });
      }

      return {
        ...state,
        loading: false,
        prospectData: action.value,
        phoneNumbers: data.phoneNumbers,
        selectedOwner: {
          label: data.assigneeName,
          value: data.assigneeId,
        },
        selectedStage: stage,
        prospectFirstName: data.firstName,
        prospectMiddleName: data.middleName,
        prospectLastName: data.lastName,
        prospectDateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : null,
        prospectWeight: data.weight,
        prospectGender: data.gender,
        prospectEmails: data.emails,
        companyName: data.companyName ?? "",
        isBusiness: data.isBusiness ?? false,
        tags: data.tags.map(
          (item) =>
            state.prospectTagOptions?.find(({ value }) => value === item) || {
              label: item,
              value: item,
            }
        ),
        primaryContactTitle: data.primaryContactTitle ?? "",
        additionalUsers: data?.additionalUsers?.length
          ? data?.additionalUsers
          : [],
        isMember: data.isMember || false,
        membership: {
          ...emptyMembership(),
          ...(data.membership || {}),
          activationDate: data.membership?.activationDate
            ? new Date(data.membership.activationDate)
            : null,
        },
        associatedWith: data.associatedWith || [],
        signedContractsCount: data.signedContractsCount || 0,
        tripsCompleted: data.tripsCompleted || 0,
        totalSpend: data.totalSpend || 0,
        firstFlight: data.firstFlight || null,
        totalFlightHours: data.totalFlightHours || "",
        totalFlightDistance: data.totalFlightDistance || 0,
        timesAroundWorld: data.timesAroundWorld || 0,
        passengers: data.passengers || [],
        notes: data.notes || [],
        addresses: data.addresses || [],
        pets: data.pets || [],
        isVerified: data.isVerified || false,
        idDocumentsUploadedAt: data.idDocumentsUploadedAt || null,
        isAppUser: !!data.appUser?.id || !!data.leadData.find(({ appUser }) => !!appUser?.id),
        iosAppBanned: data.iosAppBanned ?? false,
      };
    }
    default:
      break;
  }
  return state;
};

const SingleProspectActionsTableActions = ({
  field,
  saving,
  editableField,
  handleEditField,
  handleEditFieldCancel,
  handleSubmit,
}) => {
  const onEdit = useCallback(() => {
    handleEditField(field);
  }, [field, handleEditField]);

  const onCancel = useCallback(() => {
    handleEditFieldCancel(field);
  }, [field, handleEditFieldCancel]);

  return (
    <td>
      {editableField !== field && (
        <button
          className="base-button icon-button evo-blue"
          onClick={onEdit}
          disabled={!!editableField}
        >
          <span className="material-icons">edit</span>
        </button>
      )}
      {editableField === field && (
        <button
          className="base-button icon-button btn-success"
          onClick={handleSubmit}
          disabled={saving}
        >
          <span className="material-icons">check_circle</span>
        </button>
      )}
      {editableField === field && (
        <button
          className="base-button icon-button btn-danger ml-2"
          onClick={onCancel}
          disabled={saving}
        >
          <span className="material-icons">cancel</span>
        </button>
      )}
    </td>
  );
};

const SingleProspectContent = () => {
  const { id } = useParams();
  const history = useHistory();

  const initialState = {
    loading: true,
    saving: false,
    editableField: null,
    editableFieldPreviousValue: null,
    phoneEnabled: false,
    ownerFieldEnabled: false,
    tagFieldEnabled: false,
    stageFieldEnabled: false,
    prospectData: null,
    prospectFirstName: "",
    prospectMiddleName: "",
    prospectLastName: "",
    prospectDateOfBirth: null,
    prospectWeight: "",
    prospectGender: null,
    prospectEmails: [""],
    additionalUsers: [],
    phoneNumbers: [
      {
        countryCode: "US",
        number: "",
        phoneType: "mobile",
        order: 1,
      },
    ],
    prospectTagOptions: [
      { label: "RFP", value: "rfp" },
      { label: "Phone Call", value: "phoneCall" },
    ],
    selectedStage: null,
    selectedOwner: null,
    ownerOptions: [],
    tags: [],
    stageOptions: [
      {
        label: "New Prospect",
        value: "newLead",
        stageId: 11,
      },
      {
        label: "In Progress",
        value: "inProgress",
        stageId: 2,
      },
      {
        label: "Archived",
        value: "archived",
        stageId: 8,
      },
      {
        label: "Warm Prospect",
        value: "warmProspect",
        stageId: 7,
      },
      {
        label: "Hot Prospect",
        value: "hotProspect",
        stageId: 15,
      },
      {
        label: "Repeat Client",
        value: "repeatClient",
        stageId: 4,
      },
    ],
    isBusiness: false,
    companyName: "",
    primaryContactTitle: "",
    isMember: false,
    membership: emptyMembership(),
    membershipModalOpen: false,
    associatedWith: [],
    signedContractsCount: 0,
    tripsCompleted: 0,
    totalSpend: 0,
    firstFlight: null,
    totalFlightHours: "",
    totalFlightDistance: 0,
    timesAroundWorld: 0,
    passengers: [],
    notes: [],
    addresses: [],
    pets: [],
    isVerified: false,
    idDocumentsUploadedAt: null,
    iosAppBanned: false,
  };

  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdminOrAd = userRoles.some((role) => {
    return "Super Admin" === role || "Account Director" === role;
  });

  const superAdmin = userRoles.some((role) => "Super Admin" === role);

  const salesAssistant = userRoles.some((role) => {
    return "Sales Assistant" === role;
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    loadOwnerOptions();
    loadProspectData(id);

    document.title = "Edit Prospect | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOwnerOptions = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai.auth(token).get(`/api/users/roles/dynamicSales`);

    if (data.users.length) {
      let results = data.users.map((user) => {
        return { value: user.user_id, label: user.name, email: user.email };
      });

      dispatch({ type: "fieldChanged", field: "ownerOptions", value: results });

      return results;
    } else {
      return [];
    }
  };

  const loadProspectData = async (prospectId) => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    let { data } = await ai
      .auth(token)
      .get(`/api/prospects/${prospectId}`)
      .catch((err) => {
        toast.error("Unauthorized!", toastOptions);
        history.push("/prospects");
        return err.response;
      });

    if (data) {
      dispatch({ type: "dataDownloaded", value: data });
    } else {
      toast.error("Unauthorized!", toastOptions);
      history.push("/prospects");
    }
  };

  const handleStageSelection = async (selectedStage) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedStage",
      value: selectedStage,
    });
    return selectedStage;
  };

  const handleOwnerSelection = (selectedOwner) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedOwner",
      value: selectedOwner,
    });
    return selectedOwner;
  };

  const handleTextInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleSubmit = async (e, data = {}) => {
    e?.preventDefault();

    if (!state.prospectFirstName || !state.prospectLastName) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }
    if (state.prospectDateOfBirth && (moment(state.prospectDateOfBirth).isAfter(moment(), "day") || moment(state.prospectDateOfBirth).isBefore(moment('1900-01-01'), "day"))) {
      return toast.error(
        "Please fill valid date of birth and retry.",
        toastOptions,
      );
    }
    if (state.prospectWeight && (state.prospectWeight < 2 || state.prospectWeight > 660 || !/^-?\d+(\.\d{1})?$/.test(state.prospectWeight))) {
      return toast.error(
        "Please fill valid weight and retry.",
        toastOptions,
      );
    }

    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    const updateBody = {
      firstName: state.prospectFirstName,
      middleName: state.prospectMiddleName,
      lastName: state.prospectLastName,
      dateOfBirth: state.prospectDateOfBirth || null,
      weight: state.prospectWeight || null,
      gender: state.prospectGender || null,
      emails: state.prospectEmails,
      countryCode: state.prospectCountryCode,
      stage: state.selectedStage?.value,
      tags: state.tags?.length ? state.tags.map((item) => item.value) : [],
      assigneeName: state?.selectedOwner?.label,
      assigneeId: state?.selectedOwner?.value,
      phoneNumbers: [...state.phoneNumbers],
      isBusiness: state.isBusiness,
      companyName: state.companyName,
      additionalUsers: state?.additionalUsers ?? [],
      primaryContactTitle: state.primaryContactTitle,
      isMember: state.isMember,
      membership: {
        activationDate: state.membership?.activationDate || null,
        depositTier: state.membership?.depositTier || null,
        benefitsTier: state.membership?.benefitsTier || null,
        activationCreditPercent:
          state.membership?.activationCreditPercent ?? null,
        clickToFlyPercent: state.membership?.clickToFlyPercent ?? null,
        referralBonusPercent:
          state.membership?.referralBonusPercent ?? null,
      },
      passengers: state.passengers,
      notes: state.notes,
      addresses: state.addresses,
      pets: state.pets,
      isVerified: state.isVerified,
      iosAppBanned: state.iosAppBanned,
      ...(data || {}),
    };

    updateBody.additionalUsers = updateBody.additionalUsers.map((user) => ({
      ...user,
      relationship: user?.relationship?.value,
      isAuthorizedSigner: true,
    }));

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    let response = await ai
      .auth(token)
      .put(`/api/prospects/${id}`, updateBody)
      .catch((error) => {
        Sentry.captureException(error);
        toast.error(getApiErrorMessage(error, "Couldn't save the prospect."), toastOptions);
      });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      if (response.data?.prospectExisted) {
        toast.warning(
          "This prospect existed already. No prospect data has been updated.",
          toastOptions
        );
        return;
      }

      dispatch({
        type: "fieldChanged",
        field: "editableField",
        value: null,
      });

      dispatch({
        type: "fieldChanged",
        field: "editableFieldPreviousValue",
        value: null,
      });

      dispatch({
        type: "fieldChanged",
        field: "prospectData",
        value: {
          ...(state.prospectData || {}),
          firstName: response.data?.prospectData?.firstName,
          middleName: response.data?.prospectData?.middleName,
          lastName: response.data?.prospectData?.lastName,
          assigneeId: response.data?.prospectData?.assigneeId,
          assigneeName: response.data?.prospectData?.assigneeName,
        },
      });

      dispatch({
        type: "fieldChanged",
        field: "addresses",
        value: response.data?.prospectData?.addresses || [],
      });

      dispatch({
        type: "fieldChanged",
        field: "prospectEmails",
        value: response.data?.prospectData?.emails || [],
      });

      dispatch({
        type: "fieldChanged",
        field: "phoneNumbers",
        value: response.data?.prospectData?.phoneNumbers || [],
      });

      dispatch({
        type: "fieldChanged",
        field: "additionalUsers",
        value: (response.data?.prospectData?.additionalUsers || [])?.map(
          (user) => ({
            ...user,
            relationship: user.relationship
              ? {
                  label: user.relationship,
                  value: user.relationship,
                }
              : null,
          })
        ),
      });

      dispatch({
        type: "fieldChanged",
        field: "passengers",
        value: response.data?.prospectData?.passengers || [],
      });

      dispatch({
        type: "fieldChanged",
        field: "notes",
        value: response.data?.prospectData?.notes || [],
      });

      dispatch({
        type: "fieldChanged",
        field: "pets",
        value: response.data?.prospectData?.pets || [],
      });

      toast.success(
        `Prospect ${response.data?.prospectData?.firstName} ${response.data?.prospectData?.lastName} was successfully updated.`,
        toastOptions
      );
    }
  };

  const handleCheckboxChange = (e) => {
    if (e.target.name === "isMember" && e.target.checked) {
      dispatch({
        type: "fieldChanged",
        field: "membershipModalOpen",
        value: true,
      });
      return;
    }
    dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value: e.target.checked,
    });
    handleSubmit(e, {
      [e.target.name]: e.target.checked,
    });
  };

  const handleMembershipModalCancel = () => {
    dispatch({
      type: "fieldChanged",
      field: "membershipModalOpen",
      value: false,
    });
  };

  const handleMembershipModalConfirm = (membershipFromModal) => {
    const normalizedMembership = {
      activationDate: membershipFromModal.activationDate || null,
      depositTier: membershipFromModal.depositTier || null,
      benefitsTier: membershipFromModal.benefitsTier || null,
      activationCreditPercent:
        membershipFromModal.activationCreditPercent ?? null,
      clickToFlyPercent: membershipFromModal.clickToFlyPercent ?? null,
      referralBonusPercent: membershipFromModal.referralBonusPercent ?? null,
    };
    dispatch({
      type: "fieldChanged",
      field: "membership",
      value: normalizedMembership,
    });
    dispatch({ type: "fieldChanged", field: "isMember", value: true });
    dispatch({
      type: "fieldChanged",
      field: "membershipModalOpen",
      value: false,
    });
    handleSubmit(null, {
      isMember: true,
      membership: normalizedMembership,
    });
  };

  const handleMembershipFieldChange = (field, value) => {
    dispatch({
      type: "fieldChanged",
      field: "membership",
      value: {
        ...(state.membership || emptyMembership()),
        [field]: value,
      },
    });
  };

  const handleBenefitsTierChange = (option) => {
    const tier = option?.value || null;
    const next = {
      ...(state.membership || emptyMembership()),
      benefitsTier: tier,
    };
    if (tier && MEMBERSHIP_TIER_DEFAULTS[tier]) {
      const defaults = MEMBERSHIP_TIER_DEFAULTS[tier];
      next.activationCreditPercent = defaults.activationCreditPercent;
      next.clickToFlyPercent = defaults.clickToFlyPercent;
      next.referralBonusPercent = defaults.referralBonusPercent;
    }
    dispatch({
      type: "fieldChanged",
      field: "membership",
      value: next,
    });
  };

  const snapToHalfPercent = (value) => {
    if (value === null || value === undefined) return null;
    const snapped = Math.round(value / 0.005) * 0.005;
    return +snapped.toFixed(4);
  };

  const handleMembershipPercentChange = (field, option) => {
    if (!option) {
      handleMembershipFieldChange(field, null);
      return;
    }
    let value = option.value;
    if (option.__isNew__) {
      const parsed = parsePercentInput(option.value);
      if (parsed === null) {
        toast.error("Enter a number between 0 and 10%.", toastOptions);
        return;
      }
      const snapped = snapToHalfPercent(parsed);
      if (!isValidMembershipPercent(snapped) || Math.abs(snapped - parsed) > 1e-6) {
        toast.error(
          "Membership % must be 0–10% in 0.5% increments.",
          toastOptions,
        );
        return;
      }
      value = snapped;
    }
    if (!isValidMembershipPercent(value)) {
      toast.error(
        "Membership % must be 0–10% in 0.5% increments.",
        toastOptions,
      );
      return;
    }
    handleMembershipFieldChange(field, value);
  };

  const handleEditField = (field) => {
    dispatch({
      type: "fieldChanged",
      field: "editableField",
      value: field,
    });
    dispatch({
      type: "fieldChanged",
      field: "editableFieldPreviousValue",
      value: state[field],
    });
  };

  const handleEditFieldCancel = (field) => {
    dispatch({
      type: "fieldChanged",
      field: "editableField",
      value: null,
    });

    if (field === "name") {
      dispatch({
        type: "fieldChanged",
        field: "prospectFirstName",
        value: state.prospectData.firstName,
      });
      dispatch({
        type: "fieldChanged",
        field: "prospectMiddleName",
        value: state.prospectData.middleName,
      });
      dispatch({
        type: "fieldChanged",
        field: "prospectLastName",
        value: state.prospectData.lastName,
      });
      dispatch({
        type: "fieldChanged",
        field: "prospectDateOfBirth",
        value: state.prospectData.dateOfBirth,
      });
      dispatch({
        type: "fieldChanged",
        field: "prospectWeight",
        value: state.prospectData.weight,
      });
      dispatch({
        type: "fieldChanged",
        field: "prospectGender",
        value: state.prospectData.gender,
      });
    } else {
      dispatch({
        type: "fieldChanged",
        field,
        value: state.editableFieldPreviousValue,
      });
      dispatch({
        type: "fieldChanged",
        field: "editableFieldPreviousValue",
        value: null,
      });
    }
  };

  const handleToggleEditInModal = (modal) => {
    dispatch({
      type: "toggleEditInModal",
      modal,
    });
  };

  const handleProspectTagSelection = (selectedTags) => {
    dispatch({
      type: "fieldChanged",
      field: "tags",
      value: selectedTags,
    });
    return selectedTags;
  };

  const handleProspectAddressesChange = (addresses) =>
    handleSubmit(null, {
      addresses,
    });

  const handleProspectEmailsChange = (emails) =>
    handleSubmit(null, {
      emails,
    });

  const handleProspectPhoneNumbersChange = (phoneNumbers) =>
    handleSubmit(null, {
      phoneNumbers,
    });

  const handleProspectPassengersChange = (passengers) =>
    handleSubmit(null, {
      passengers,
    });

  const handleProspectPetsChange = (pets) =>
    handleSubmit(null, {
      pets,
    });

  const handleProspectNotesChange = (notes) =>
    handleSubmit(null, {
      notes,
    });

  const handleProspectAuthorizedSignersChange = (additionalUsers) =>
    handleSubmit(null, {
      additionalUsers,
    });

  if (state.loading) {
    return <Loader />;
  }

  return (
    <Fragment>
      <ProspectHeader data={state.prospectData || {}} />

      <UnsavedChangesWarningModal isChanged={!!state.editableField} />

      <MembershipActivationModal
        isOpen={!!state.membershipModalOpen}
        existing={state.membership}
        onConfirm={handleMembershipModalConfirm}
        onCancel={handleMembershipModalCancel}
      />

      <Grid container spacing={4} className="mt-10">
        <Grid item xs={12} sm={12} md={6} lg={5}>
          <div className="prospect-table prospect-actions-table">
            <table>
              <thead>
                <tr>
                  <th colSpan={3}>Prospect Actions</th>
                </tr>
              </thead>
              <tbody>
                {(superAdminOrAd || salesAssistant) && (
                  <tr>
                    <td>Owner</td>
                    <td>
                      {state.editableField === "selectedOwner" ? (
                        <Select
                          key={`selectedOwner-${state.selectedOwner}`}
                          className={`prospect-owner-select ${
                            state.ownerFieldEnabled
                              ? ""
                              : "selectDisabledTillClicked"
                          }`}
                          classNamePrefix="select"
                          value={state.selectedOwner}
                          options={state.ownerOptions}
                          placeholder="Prospect Owner"
                          onChange={handleOwnerSelection}
                          name="selectedOwner"
                          onFocus={() =>
                            dispatch({
                              type: "fieldChanged",
                              field: "ownerFieldEnabled",
                              value: true,
                            })
                          }
                        />
                      ) : (
                        state.selectedOwner?.label
                      )}
                    </td>
                    <SingleProspectActionsTableActions
                      field="selectedOwner"
                      saving={state.saving}
                      editableField={state.editableField}
                      handleEditField={handleEditField}
                      handleEditFieldCancel={handleEditFieldCancel}
                      handleSubmit={handleSubmit}
                    />
                  </tr>
                )}
                <tr>
                  <td>Stage</td>
                  <td>
                    {state.editableField === "selectedStage" ? (
                      <Select
                        key={`stage-${state.selectedStage}`}
                        className={`stage-select ${
                          state.stageFieldEnabled
                            ? ""
                            : "selectDisabledTillClicked"
                        }`}
                        classNamePrefix="select"
                        value={state.selectedStage}
                        options={state.stageOptions}
                        placeholder="Prospect Stage"
                        onChange={handleStageSelection}
                        name="selectedStage"
                        onFocus={() =>
                          dispatch({
                            type: "fieldChanged",
                            field: "stageFieldEnabled",
                            value: true,
                          })
                        }
                      />
                    ) : (
                      state.selectedStage?.label
                    )}
                  </td>
                  <SingleProspectActionsTableActions
                    field="selectedStage"
                    saving={state.saving}
                    editableField={state.editableField}
                    handleEditField={handleEditField}
                    handleEditFieldCancel={handleEditFieldCancel}
                    handleSubmit={handleSubmit}
                  />
                </tr>
                <tr>
                  <td>Contact Tags</td>
                  <td>
                    {state.editableField === "tags" ? (
                      <CreatableSelect
                        key={`tags-${state.tags && state.tags[0]}`}
                        className={`tag-select ${
                          state.tagFieldEnabled
                            ? ""
                            : "selectDisabledTillClicked"
                        }`}
                        classNamePrefix="select"
                        isMulti
                        value={state.tags}
                        options={state.prospectTagOptions}
                        placeholder="Prospect Tags"
                        onChange={handleProspectTagSelection}
                        name="tags"
                        onFocus={() =>
                          dispatch({
                            type: "fieldChanged",
                            field: "tagFieldEnabled",
                            value: true,
                          })
                        }
                      />
                    ) : (
                      state.tags
                        ?.map((item) => item.label || item.value)
                        ?.join(", ")
                    )}
                  </td>
                  <SingleProspectActionsTableActions
                    field="tags"
                    saving={state.saving}
                    editableField={state.editableField}
                    handleEditField={handleEditField}
                    handleEditFieldCancel={handleEditFieldCancel}
                    handleSubmit={handleSubmit}
                  />
                </tr>
                <tr>
                  <td>App user?</td>
                  <td>{state.isAppUser ? 'Yes' : 'No'}</td>
                  <td/>
                </tr>
                {/* Written by the booking system when an identity document is
                    uploaded from the app or the website. Read-only here, and
                    deliberately left out of the save payload. */}
                <tr>
                  <td>ID on file?</td>
                  <td>
                    {state.idDocumentsUploadedAt
                      ? moment(state.idDocumentsUploadedAt).format("MMM D, YYYY")
                      : "No"}
                  </td>
                  <td/>
                </tr>
                <tr>
                  <td>Evolved Member?</td>
                  <td>
                    <Checkbox
                      checked={state.isMember}
                      onChange={handleCheckboxChange}
                      name="isMember"
                    />
                  </td>
                  <td />
                </tr>
                {state.isMember && (
                  <Fragment>
                    <tr>
                      <td colSpan={3} className="membership-section-header">
                        <strong>Membership Details</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>Activation Date</td>
                      <td>
                        {state.editableField === "membershipActivationDate" ? (
                          <DatePicker
                            className="prospect-date-picker"
                            name="membershipActivationDate"
                            onChange={(date) =>
                              handleMembershipFieldChange(
                                "activationDate",
                                date
                              )
                            }
                            value={state.membership?.activationDate || null}
                          />
                        ) : (
                          <span>
                            {state.membership?.activationDate
                              ? moment(state.membership.activationDate).format(
                                  "M/D/YYYY"
                                )
                              : "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipActivationDate"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                    <tr>
                      <td>Deposit Tier</td>
                      <td>
                        {state.editableField === "membershipDepositTier" ? (
                          <Select
                            className="membership-deposit-select"
                            classNamePrefix="select"
                            options={DEPOSIT_TIER_OPTIONS}
                            value={
                              DEPOSIT_TIER_OPTIONS.find(
                                ({ value }) =>
                                  value === state.membership?.depositTier
                              ) || null
                            }
                            isClearable
                            placeholder="Deposit Tier"
                            onChange={(option) =>
                              handleMembershipFieldChange(
                                "depositTier",
                                option?.value || null
                              )
                            }
                          />
                        ) : (
                          <span>
                            {DEPOSIT_TIER_OPTIONS.find(
                              ({ value }) =>
                                value === state.membership?.depositTier
                            )?.label || "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipDepositTier"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                    <tr>
                      <td>Benefits Tier</td>
                      <td>
                        {state.editableField === "membershipBenefitsTier" ? (
                          <Select
                            className="membership-benefits-select"
                            classNamePrefix="select"
                            options={BENEFITS_TIER_OPTIONS}
                            value={
                              BENEFITS_TIER_OPTIONS.find(
                                ({ value }) =>
                                  value === state.membership?.benefitsTier
                              ) || null
                            }
                            isClearable
                            placeholder="Benefits Tier"
                            onChange={handleBenefitsTierChange}
                          />
                        ) : (
                          <span>
                            {BENEFITS_TIER_OPTIONS.find(
                              ({ value }) =>
                                value === state.membership?.benefitsTier
                            )?.label || "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipBenefitsTier"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                    <tr>
                      <td>Activation Credit %</td>
                      <td>
                        {state.editableField ===
                        "membershipActivationCreditPercent" ? (
                          <CreatableSelect
                            className="membership-percent-select"
                            classNamePrefix="select"
                            options={MEMBERSHIP_PERCENT_OPTIONS}
                            value={
                              state.membership?.activationCreditPercent != null
                                ? {
                                    value:
                                      state.membership.activationCreditPercent,
                                    label: formatPercent(
                                      state.membership.activationCreditPercent
                                    ),
                                  }
                                : null
                            }
                            isClearable
                            isDisabled={!state.membership?.benefitsTier}
                            placeholder={
                              state.membership?.benefitsTier
                                ? "Select %"
                                : "Pick Benefits Tier first"
                            }
                            onChange={(option) =>
                              handleMembershipPercentChange(
                                "activationCreditPercent",
                                option
                              )
                            }
                            formatCreateLabel={(input) => `Use "${input}"`}
                          />
                        ) : (
                          <span>
                            {formatPercent(
                              state.membership?.activationCreditPercent
                            ) || "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipActivationCreditPercent"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                    <tr>
                      <td>Click-to-Fly %</td>
                      <td>
                        {state.editableField ===
                        "membershipClickToFlyPercent" ? (
                          <CreatableSelect
                            className="membership-percent-select"
                            classNamePrefix="select"
                            options={MEMBERSHIP_PERCENT_OPTIONS}
                            value={
                              state.membership?.clickToFlyPercent != null
                                ? {
                                    value: state.membership.clickToFlyPercent,
                                    label: formatPercent(
                                      state.membership.clickToFlyPercent
                                    ),
                                  }
                                : null
                            }
                            isClearable
                            isDisabled={!state.membership?.benefitsTier}
                            placeholder={
                              state.membership?.benefitsTier
                                ? "Select %"
                                : "Pick Benefits Tier first"
                            }
                            onChange={(option) =>
                              handleMembershipPercentChange(
                                "clickToFlyPercent",
                                option
                              )
                            }
                            formatCreateLabel={(input) => `Use "${input}"`}
                          />
                        ) : (
                          <span>
                            {formatPercent(
                              state.membership?.clickToFlyPercent
                            ) || "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipClickToFlyPercent"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                    <tr>
                      <td>Referral Bonus %</td>
                      <td>
                        {state.editableField ===
                        "membershipReferralBonusPercent" ? (
                          <CreatableSelect
                            className="membership-percent-select"
                            classNamePrefix="select"
                            options={MEMBERSHIP_PERCENT_OPTIONS}
                            value={
                              state.membership?.referralBonusPercent != null
                                ? {
                                    value:
                                      state.membership.referralBonusPercent,
                                    label: formatPercent(
                                      state.membership.referralBonusPercent
                                    ),
                                  }
                                : null
                            }
                            isClearable
                            isDisabled={!state.membership?.benefitsTier}
                            placeholder={
                              state.membership?.benefitsTier
                                ? "Select %"
                                : "Pick Benefits Tier first"
                            }
                            onChange={(option) =>
                              handleMembershipPercentChange(
                                "referralBonusPercent",
                                option
                              )
                            }
                            formatCreateLabel={(input) => `Use "${input}"`}
                          />
                        ) : (
                          <span>
                            {formatPercent(
                              state.membership?.referralBonusPercent
                            ) || "-"}
                          </span>
                        )}
                      </td>
                      <SingleProspectActionsTableActions
                        field="membershipReferralBonusPercent"
                        saving={state.saving}
                        editableField={state.editableField}
                        handleEditField={handleEditField}
                        handleEditFieldCancel={handleEditFieldCancel}
                        handleSubmit={handleSubmit}
                      />
                    </tr>
                  </Fragment>
                )}
                <tr>
                  <td>Is Verified?</td>
                  <td>
                    <Checkbox
                      checked={state.isVerified}
                      onChange={handleCheckboxChange}
                      name="isVerified"
                    />
                  </td>
                  <td />
                </tr>
                <tr>
                  <td>Business client?</td>
                  <td>
                    <Checkbox
                      checked={state.isBusiness}
                      onChange={handleCheckboxChange}
                      name="isBusiness"
                    />
                  </td>
                  <td />
                </tr>

                {state.isBusiness && (
                  <tr>
                    <td>Company Name</td>
                    <td>
                      {state.editableField === "companyName" ? (
                        <TextInput
                          name="companyName"
                          handleChange={handleTextInputChange}
                          value={state.companyName}
                          disabledTillClicked
                        />
                      ) : (
                        state.companyName || "-"
                      )}
                    </td>
                    <SingleProspectActionsTableActions
                      field="companyName"
                      saving={state.saving}
                      editableField={state.editableField}
                      handleEditField={handleEditField}
                      handleEditFieldCancel={handleEditFieldCancel}
                      handleSubmit={handleSubmit}
                    />
                  </tr>
                )}
                {state.isBusiness && (
                  <tr>
                    <td>Primary Signer's Title</td>
                    <td>
                      {state.editableField === "primaryContactTitle" ? (
                        <TextInput
                          name="primaryContactTitle"
                          handleChange={handleTextInputChange}
                          value={state.primaryContactTitle}
                          disabledTillClicked
                        />
                      ) : (
                        state.primaryContactTitle || "-"
                      )}
                    </td>
                    <SingleProspectActionsTableActions
                      field="primaryContactTitle"
                      saving={state.saving}
                      editableField={state.editableField}
                      handleEditField={handleEditField}
                      handleEditFieldCancel={handleEditFieldCancel}
                      handleSubmit={handleSubmit}
                    />
                  </tr>
                )}
                <tr>
                  <td>iOS App Banned?</td>
                  <td>
                    <Checkbox
                      checked={state.iosAppBanned}
                      onChange={handleCheckboxChange}
                      name="iosAppBanned"
                      disabled={!superAdmin}
                    />
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
          <div className="prospect-table prospect-summary-table">
            <table>
              <thead>
                <tr>
                  <th colSpan={2}>Booking Statistics</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Signed Contracts</td>
                  <td>{state.signedContractsCount || 0}</td>
                </tr>
                <tr>
                  <td>Trips completed</td>
                  <td>{state.tripsCompleted || 0}</td>
                </tr>
                <tr>
                  <td>First Flight</td>
                  <td>
                    {state.firstFlight
                      ? moment(state.firstFlight).format("MMM D, YYYY")
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <td>Total Spend</td>
                  <td>{currencyFormatter.format(state.totalSpend || 0)}</td>
                </tr>
                <tr>
                  <td>Total Flight Hours</td>
                  <td>{state.totalFlightHours || "N/A"}</td>
                </tr>
                <tr>
                  <td>Total Flight Distance</td>
                  <td>
                    {state.totalFlightDistance
                      ? `${state.totalFlightDistance.toLocaleString()} mi`
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <td>Times Around World</td>
                  <td>{state.timesAroundWorld || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Grid>

        <Grid item xs={12} sm={12} md={4} lg={2}>
          <div className="prospect-table prospect-summary-table">
            <table>
              <thead>
                <tr>
                  <th colSpan={2}>Associated With</th>
                </tr>
              </thead>
              <tbody>
                {!state.associatedWith.length && (
                  <tr>
                    <td colSpan={2}>N/A</td>
                  </tr>
                )}
                {state.associatedWith.map((companyName) => (
                  <tr key={companyName}>
                    <td colSpan={2}>{companyName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Grid>
      </Grid>

      <Grid container className="mt-10" spacing={4}>
        <Grid item container xs={12} sm={12} lg={6}>
          <Grid item xs={12} sm={12} className="w-100">
            <div className="prospect-table prospect-contact-information-table">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3}>Contact information</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr className="heading-row">
                    <td>
                      First Name <span className="required">*</span>
                    </td>
                    <td>Middle Name</td>
                    <td>
                      Last Name <span className="required">*</span>
                    </td>
                    <td />
                  </tr>
                  <tr className="fields-row">
                    <td>
                      {state.editableField === "name" ? (
                        <TextInput
                          className="base-input"
                          name="prospectFirstName"
                          handleChange={handleTextInputChange}
                          value={state.prospectFirstName}
                        />
                      ) : (
                        <span>{state.prospectFirstName}</span>
                      )}
                    </td>
                    <td>
                      {state.editableField === "name" ? (
                        <TextInput
                          className="base-input"
                          name="prospectMiddleName"
                          handleChange={handleTextInputChange}
                          value={state.prospectMiddleName}
                        />
                      ) : (
                        <span>{state.prospectMiddleName}</span>
                      )}
                    </td>
                    <td>
                      {state.editableField === "name" ? (
                        <TextInput
                          className="base-input"
                          name="prospectLastName"
                          handleChange={handleTextInputChange}
                          value={state.prospectLastName}
                        />
                      ) : (
                        <span>{state.prospectLastName}</span>
                      )}
                    </td>

                    <SingleProspectActionsTableActions
                      field="name"
                      saving={state.saving}
                      editableField={state.editableField}
                      handleEditField={handleEditField}
                      handleEditFieldCancel={handleEditFieldCancel}
                      handleSubmit={handleSubmit}
                    />
                  </tr>
                  <tr className="heading-row">
                    <td>
                      Date of Birth
                    </td>
                    <td>Weight (lb)</td>
                    <td>
                      Gender
                    </td>
                    <td />
                  </tr>
                  <tr className="fields-row">
                    <td>
                      {state.editableField === "name" ? (
                        <DatePicker
                          className="prospect-date-picker"
                          name="dateOfBirth"
                          onChange={(date) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "prospectDateOfBirth",
                              value: date,
                            });
                          }}
                          value={state.prospectDateOfBirth}
                        />
                      ) : (
                        <span>
                          {state.prospectDateOfBirth ? moment(state.prospectDateOfBirth).format("M/D/YYYY") : ""}
                        </span>
                      )}
                    </td>
                    <td>
                      {state.editableField === "name" ? (
                        <TextInput
                          containerClasses="mb-0"
                          className="base-input"
                          name="prospectWeight"
                          handleChange={handleTextInputChange}
                          value={state.prospectWeight}
                          type="number"
                          min="2"
                          max="660"
                          step="0.1"
                        />
                      ) : (
                        <span>{state.prospectWeight ? `${state.prospectWeight} (lb)` : ""}</span>
                      )}
                    </td>
                    <td>
                      {state.editableField === "name" ? (
                        <Select
                          className="gender-select"
                          classNamePrefix="select"
                          options={genders}
                          placeholder="Gender"
                          onChange={(gender) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "prospectGender",
                              value: gender?.value || null,
                            });
                          }}
                          value={genders.find(({ value }) => value === state.prospectGender) || null}
                          isClearable
                        />
                      ) : (
                        <span>{state.prospectGender}</span>
                      )}
                    </td>

                    <td />
                  </tr>

                  <SingleProspectAddressesTable
                    addresses={state.addresses}
                    handleChange={handleProspectAddressesChange}
                    handleToggleEdit={handleToggleEditInModal}
                    disabled={!!state.editableField}
                  />

                  <SingleProspectEmailsTable
                    emails={state.prospectEmails}
                    handleChange={handleProspectEmailsChange}
                    handleToggleEdit={handleToggleEditInModal}
                    disabled={!!state.editableField}
                  />

                  <SingleProspectPhonesTable
                    phoneNumbers={state.phoneNumbers}
                    handleChange={handleProspectPhoneNumbersChange}
                    handleToggleEdit={handleToggleEditInModal}
                    disabled={!!state.editableField}
                  />

                  <SingleProspectAuthorizedSignersTable
                    authorizedSigners={state.additionalUsers}
                    handleChange={handleProspectAuthorizedSignersChange}
                    handleToggleEdit={handleToggleEditInModal}
                    disabled={!!state.editableField}
                  />
                </tbody>
              </table>
            </div>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12} lg={6} container rowSpacing={4}>
          <Grid item xs={12} sm={12} className="w-100">
            <SingleProspectPassengersTable
              passengers={state.passengers}
              handleChange={handleProspectPassengersChange}
              handleToggleEdit={handleToggleEditInModal}
              disabled={!!state.editableField}
            />

            <SingleProspectPetsTable
              pets={state.pets}
              handleChange={handleProspectPetsChange}
              handleToggleEdit={handleToggleEditInModal}
              disabled={!!state.editableField}
            />

            <SingleProspectNotesTable
              notes={state.notes}
              handleChange={handleProspectNotesChange}
              handleToggleEdit={handleToggleEditInModal}
              disabled={!!state.editableField}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12}>
          <SingleProspectTripHistory />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default SingleProspectContent;
